<template>
  <v-container>
    <h1 class="mb-4">Orphaned Users</h1>

    <v-card>
      <v-card-text>
        <div v-if="loading" class="text-center pa-4">Loading...</div>
        <div v-else>
          <div v-if="orphaned.length === 0" class="text-center pa-4">No orphaned users found.</div>

          <v-data-table
            v-else
            :headers="headers"
            :items="orphaned"
            item-value="id"
            class="elevation-1"
            :items-per-page="10"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn color="red" small @click="requestDeleteUser(item)">Delete</v-btn>
            </template>
            <template v-slot:[`item.permissions`]="{ item }">
              <span>{{ JSON.stringify(item.permissions) }}</span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm User Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to permanently delete the user <strong>{{ userToDelete?.alias }}</strong>? This will remove their Firestore document but cannot delete the Auth user here (server-side).
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { firebaseApp, auth } from '@/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

const orphaned = ref([]);
const loading = ref(false);
const deleteDialog = ref(false);
const userToDelete = ref(null);

const headers = [
  { title: 'Alias', key: 'alias' },
  { title: 'User ID', key: 'user_id' },
  { title: 'Permissions', key: 'permissions' },
  { title: 'Actions', key: 'actions', sortable: false }
];

// Explicitly set the Functions region to match your project deployment.
// This can avoid the client attempting to call the wrong URL and running into CORS/preflight issues.
const functions = getFunctions(firebaseApp, 'us-central1');
const listOrphanedUsers = httpsCallable(functions, 'listOrphanedUsers');
const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');

async function load() {
  loading.value = true;
  try {
    if (!auth || !auth.currentUser) {
      throw new Error('You must be signed in to load orphaned users.');
    }

    const resp = await listOrphanedUsers();
    orphaned.value = resp.data.orphaned || [];
  } catch (err) {
    console.error('Error calling listOrphanedUsers:', err);
    orphaned.value = [];
  } finally {
    loading.value = false;
  }
}

function requestDeleteUser(user) {
  userToDelete.value = user;
  deleteDialog.value = true;
}

async function confirmDelete() {
  if (!userToDelete.value) return;
  try {
    // Call server function to delete Auth user + Firestore user doc.
    const resp = await deleteUserAccount({ userId: userToDelete.value.user_id });
    if (resp.data && resp.data.success) {
      await load();
    } else {
      console.error('deleteUserAccount returned unexpected response:', resp);
    }
  } catch (err) {
    console.error('Error calling deleteUserAccount:', err);
    // If the callable fails due to lack of admin privilege, we do not attempt client-side deletion of the Auth user.
  }
  deleteDialog.value = false;
  userToDelete.value = null;
}

onMounted(() => {
  load();
});
</script>

<style scoped>
</style>
